import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Plus, AlertTriangle, CheckCircle, TrendingDown } from 'lucide-react';
import { budgetCategories } from '../../../data/mockData';
export function BudgetView() {
  const [showAddModal, setShowAddModal] = useState(false);
  const totalSpent = budgetCategories.reduce((s, c) => s + c.spent, 0);
  const totalBudget = budgetCategories.reduce((s, c) => s + c.limit, 0);
  const overBudget = budgetCategories.filter(c => c.spent > c.limit);
  const chartData = budgetCategories.map(c => ({
    name: c.name.split(' ')[0],
    Budget: c.limit,
    Spent: c.spent,
    over: c.spent > c.limit
  }));
  return <div className="space-y-6" data-id="element-244">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4" data-id="element-245">
        <div className="bg-white dark:bg-dark-card rounded-2xl p-5 border border-gray-100 dark:border-dark-border shadow-sm" data-id="element-246">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-247">
            Total Budget
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-248">
            ${totalBudget.toLocaleString()}
          </p>
          <p className="text-xs text-gray-400 mt-1" data-id="element-249">Monthly limit</p>
        </div>
        <div className="bg-white dark:bg-dark-card rounded-md p-5 border border-gray-100 dark:border-dark-border shadow-sm" data-id="element-250">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-251">
            Total Spent
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-252">
            ${totalSpent.toLocaleString()}
          </p>
          <p className="text-xs text-gray-400 mt-1" data-id="element-253">
            {Math.round(totalSpent / totalBudget * 100)}% of budget
          </p>
        </div>
        <div className={`rounded-2xl p-5 border shadow-sm ${overBudget.length > 0 ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-accent-50 dark:bg-accent-900/20 border-accent-200 dark:border-accent-800'}`} data-id="element-254">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-255">
            Status
          </p>
          <div className="flex items-center gap-2" data-id="element-256">
            {overBudget.length > 0 ? <AlertTriangle size={20} className="text-red-500" data-id="element-257" /> : <CheckCircle size={20} className="text-accent-500" data-id="element-258" />}
            <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-259">
              {overBudget.length > 0 ? `${overBudget.length} over` : 'On track'}
            </p>
          </div>
          <p className="text-xs text-gray-400 mt-1" data-id="element-260">
            {overBudget.length > 0 ? overBudget.map(c => c.name.split(' ')[0]).join(', ') : 'All categories within budget'}
          </p>
        </div>
      </div>

      {/* Bar chart */}
      <div className="bg-white dark:bg-dark-card rounded-2xl p-5 border border-gray-100 dark:border-dark-border shadow-sm" data-id="element-261">
        <div className="flex items-center justify-between mb-4" data-id="element-262">
          <div data-id="element-263">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-264">
              Budget vs Actual
            </h3>
            <p className="text-xs text-gray-400" data-id="element-265">February 2026</p>
          </div>
          <div className="flex items-center gap-4 text-xs" data-id="element-266">
            <div className="flex items-center gap-1.5" data-id="element-267">
              <div className="w-3 h-3 rounded-sm bg-primary-200 dark:bg-primary-800" data-id="element-268" />{' '}
              Budget
            </div>
            <div className="flex items-center gap-1.5" data-id="element-269">
              <div className="w-3 h-3 rounded-sm bg-primary-500" data-id="element-270" /> Spent
            </div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={220} data-id="element-271">
          <BarChart data={chartData} barGap={4} data-id="element-272">
            <XAxis dataKey="name" tick={{
            fontSize: 11,
            fill: '#9CA3AF'
          }} axisLine={false} tickLine={false} data-id="element-273" />
            <YAxis tick={{
            fontSize: 11,
            fill: '#9CA3AF'
          }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-274" />
            <Tooltip formatter={(v: any, name: string) => [`$${Number(v).toLocaleString()}`, name]} contentStyle={{
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            fontSize: '12px'
          }} data-id="element-275" />
            <Bar dataKey="Budget" fill="#BFDBFE" radius={[4, 4, 0, 0]} data-id="element-276" />
            <Bar dataKey="Spent" radius={[4, 4, 0, 0]} data-id="element-277">
              {chartData.map((entry, i) => <Cell key={i} fill={entry.over ? '#EF4444' : '#1566FF'} data-id="element-278" />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Category cards */}
      <div className="flex items-center justify-between" data-id="element-279">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-280">
          Category Breakdown
        </h3>
        <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 text-sm bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-xl transition-colors font-medium" data-id="element-281">
          <Plus size={15} data-id="element-282" /> Add Category
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-id="element-283">
        {budgetCategories.map(cat => {
        const pct = Math.min(cat.spent / cat.limit * 100, 100);
        const isOver = cat.spent > cat.limit;
        const barColor = isOver ? '#EF4444' : pct > 80 ? '#F59E0B' : '#39E75F';
        const remaining = cat.limit - cat.spent;
        return <div key={cat.id} className={`bg-white dark:bg-dark-card rounded-2xl p-5 border shadow-sm transition-shadow hover:shadow-md ${isOver ? 'border-red-200 dark:border-red-800' : 'border-gray-100 dark:border-dark-border'}`} data-id="element-284">
              <div className="flex items-center justify-between mb-3" data-id="element-285">
                <div className="flex items-center gap-2.5" data-id="element-286">
                  <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-dark-hover flex items-center justify-center text-lg" data-id="element-287">
                    {cat.icon}
                  </div>
                  <div data-id="element-288">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200" data-id="element-289">
                      {cat.name}
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-290">Monthly budget</p>
                  </div>
                </div>
                {isOver && <AlertTriangle size={16} className="text-red-500 shrink-0" data-id="element-291" />}
              </div>

              <div className="mb-2" data-id="element-292">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-293">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-294">
                    Spent
                  </span>
                  <span className="font-semibold" style={{
                color: isOver ? '#EF4444' : '#374151'
              }} data-id="element-295">
                    ${cat.spent.toFixed(0)} / ${cat.limit}
                  </span>
                </div>
                <div className="h-2 bg-gray-100 dark:bg-dark-hover rounded-full overflow-hidden" data-id="element-296">
                  <div className="h-full rounded-full transition-all duration-700" style={{
                width: `${pct}%`,
                backgroundColor: barColor
              }} data-id="element-297" />
                </div>
              </div>

              <p className="text-xs" style={{
            color: isOver ? '#EF4444' : '#6B7280'
          }} data-id="element-298">
                {isOver ? `$${Math.abs(remaining).toFixed(0)} over budget` : `$${remaining.toFixed(0)} remaining (${(100 - pct).toFixed(0)}%)`}
              </p>
            </div>;
      })}
      </div>
    </div>;
}